import { CloseThin } from '@/assets/images';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import classNames from 'classnames';
import React, {
	cloneElement,
	HTMLAttributes,
	ReactElement,
	ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState
} from 'react';
import './Popup.scss';

import { sendAnalytics } from '@/services/analyticsService';

export enum PopupAlign {
	Right = 'right',
	Left = 'left'
}

export enum PopupTrigger {
	Click = 'click',
	Hover = 'hover'
}

type Props = HTMLAttributes<HTMLDivElement> & {
	text?: string | ReactNode;
	title?: string;
	containerClass?: string;
	contentClass?: string;
	img?: JSX.Element;
	onSelectionClose?: boolean;
	trigger?: ReactElement;
	event?: PopupTrigger;
	containerPaddingY?: number;
	contentPaddingX?: number;
	align?: PopupAlign;
	analytic?: string;
	user?: object;
	wide?: boolean;
};

export default function Popup({
	text,
	title,
	img,
	containerClass,
	contentClass,
	children,
	onSelectionClose,
	trigger,
	event = PopupTrigger.Click,
	containerPaddingY,
	contentPaddingX = 5,
	align,
	analytic,
	user,
	wide
}: Props): JSX.Element {
	const popupRef = useRef<HTMLDivElement>(null);

	const popupTriggerRef = useRef<HTMLButtonElement>(null);

	const popupContentRef = useRef<HTMLDivElement>(null);

	const [showPopup, setShowPopup] = useState<boolean>(false);

	const mouseLeave = useCallback(
		(evt: React.MouseEvent | MouseEvent) => {
			const triggerRect =
				popupTriggerRef.current?.getBoundingClientRect();

			if (triggerRect) {
				if (
					!(
						evt.pageX > triggerRect.x &&
						evt.pageX <
							Math.floor(triggerRect.x + triggerRect.width) &&
						evt.pageY > triggerRect.y &&
						evt.pageY <= triggerRect.y + triggerRect.height
					)
				) {
					setShowPopup(false);
					if (analytic && user && user.email) {
						sendAnalytics({
							type: 'track',
							userId: user.email,
							eventName: analytic
						});
					}
				}
			}
		},
		[popupTriggerRef.current]
	);

	useEffect(() => {
		const { current } = popupRef;

		if (current && event === 'hover') {
			current.addEventListener('mouseenter', () => setShowPopup(true));
			current.addEventListener('mouseleave', mouseLeave);
		}

		return () => {
			if (current) {
				current.removeEventListener('mouseenter', () =>
					setShowPopup(true)
				);
				current.removeEventListener('mouseleave', mouseLeave);
			}
		};
	}, [mouseLeave, event]);

	useOnClickOutside(popupRef, () => setShowPopup(false));

	return (
		<div ref={popupRef} className="popup relative flex flex-col">
			{trigger ? (
				cloneElement(trigger, {
					ref: popupTriggerRef,
					onClick:
						event === PopupTrigger.Click
							? () => setShowPopup(!showPopup)
							: undefined,
					onMouseOver:
						event === PopupTrigger.Hover
							? () => setShowPopup(true)
							: undefined
				})
			) : (
				<button
					type="button"
					className="popup-trigger flex flex-row items-center justify-between rounded px-4 md:px-2 lg:px-3"
					onClick={
						event === PopupTrigger.Click
							? () => setShowPopup(!showPopup)
							: undefined
					}
				>
					<span className="truncate">{text}</span>
					{img}
				</button>
			)}

			{showPopup && (
				<div
					className={`popup-container-wrapper${wide ? '-wide' : ''} absolute${
						align ? ` ${align}-0` : ''
					}`}
				>
					<div
						className={classNames(
							'popup-container flex flex-col items-stretch justify-between',
							{
								[`py-${containerPaddingY}`]: !!containerPaddingY
							},
							{
								'py-5': containerPaddingY === undefined
							},
							'overflow-hidden rounded-sm',
							containerClass
						)}
					>
						{title && (
							<div className="flex flex-row items-center justify-between px-5">
								<span className="popup-container--title text-lg font-medium">
									{title}
								</span>
								<button
									className="popup-container--close h-full"
									type="button"
									onClick={() => setShowPopup(false)}
								>
									<CloseThin
										width={18}
										height={18}
										className="text-white"
									/>
								</button>
							</div>
						)}
						<div
							onClick={() =>
								onSelectionClose && setShowPopup(false)
							}
							role="presentation"
							ref={popupContentRef}
							className={`flex w-full flex-grow flex-col px-${contentPaddingX} popup-container--content${
								contentClass ? ` ${contentClass}` : ''
							}`}
						>
							{children}
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
